// Shared panel for assigning individual workers to a schedule event.
// Used in ScheduleTab (jobs), EmployeesPage, CrewsPage, and CalendarPage.
import { useEffect, useState } from 'react';
import apiClient from '@lib/api/client';
import { getEmployees } from '@lib/api/employees';
import type { Employee } from '@lib/api/employees';

interface Assignment {
  id: number;
  user_id: number;
  username: string;
  email: string;
  employee_name?: string;
  role?: string;
}

interface Props {
  scheduleId: number;
  jobTitle?: string;
  scheduledDate?: string;
}

export function WorkerAssignPanel({ scheduleId, jobTitle, scheduledDate }: Props) {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [employees, setEmployees]     = useState<Employee[]>([]);
  const [selectedUid, setSelectedUid] = useState<string>('');
  const [role, setRole]               = useState('');
  const [loading, setLoading]         = useState(true);
  const [busy, setBusy]               = useState(false);
  const [error, setError]             = useState('');

  const loadAssignments = async () => {
    try {
      const r = await apiClient.get<{ success: boolean; data: Assignment[] }>(`/schedule-assignments/${scheduleId}`);
      setAssignments(r.data.data ?? []);
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    loadAssignments();
    getEmployees().then(setEmployees).catch(() => {});
  }, [scheduleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!selectedUid) return;
    setBusy(true);
    setError('');
    try {
      await apiClient.post('/schedule-assignments', { schedule_id: scheduleId, user_id: parseInt(selectedUid), role: role || undefined });
      setSelectedUid('');
      setRole('');
      loadAssignments();
    } catch { setError('Failed to assign worker.'); }
    finally { setBusy(false); }
  };

  const remove = async (id: number) => {
    setBusy(true);
    try {
      await apiClient.delete(`/schedule-assignments/${id}`);
      loadAssignments();
    } finally { setBusy(false); }
  };

  const sendNow = async () => {
    if (!assignments.length) return;
    setBusy(true);
    try {
      await apiClient.post(`/schedule-assignments/send-sms`, { schedule_id: scheduleId });
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };

  // Filter out already-assigned employees
  const assignedUids = new Set(assignments.map(a => a.user_id));
  const unassigned = employees.filter(e => e.user_id && !assignedUids.has(e.user_id));

  return (
    <div className="space-y-3">
      {jobTitle && (
        <p className="text-xs text-gray-500">
          <span className="font-medium text-gray-700">{jobTitle}</span>
          {scheduledDate && ` · ${new Date(scheduledDate + 'T12:00:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' })}`}
        </p>
      )}

      {/* Assigned workers list */}
      {loading ? (
        <div className="h-12 bg-gray-100 animate-pulse rounded-lg" />
      ) : assignments.length === 0 ? (
        <p className="text-xs text-gray-400 italic">No individual workers assigned. Crew members scheduled via crew assignment will still receive SMS.</p>
      ) : (
        <div className="space-y-1.5">
          {assignments.map(a => (
            <div key={a.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
              <div>
                <span className="text-sm font-medium text-gray-800">{a.employee_name || a.username}</span>
                {a.role && <span className="text-xs text-gray-400 ml-1.5">· {a.role}</span>}
                <span className="text-xs text-gray-400 ml-1.5">{a.email}</span>
              </div>
              <button onClick={() => remove(a.id)} disabled={busy}
                className="text-gray-400 hover:text-red-500 transition-colors ml-2">
                <span className="material-icons text-sm">person_remove</span>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add worker */}
      <div className="flex gap-2 flex-wrap">
        <select
          value={selectedUid}
          onChange={e => setSelectedUid(e.target.value)}
          className="flex-1 min-w-0 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none"
        >
          <option value="">— Add worker…</option>
          {unassigned.map(e => (
            <option key={e.user_id} value={String(e.user_id)}>
              {e.name}{e.role ? ` (${e.role})` : ''}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={role}
          onChange={e => setRole(e.target.value)}
          placeholder="Role (optional)"
          className="w-32 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none"
        />
        <button
          onClick={add}
          disabled={busy || !selectedUid}
          className="bg-ochre-600 text-white rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-ochre-700 disabled:opacity-40 whitespace-nowrap"
        >
          <span className="material-icons text-sm align-middle">person_add</span>
        </button>
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      {/* Send test SMS now */}
      {assignments.length > 0 && (
        <button
          onClick={sendNow}
          disabled={busy}
          className="flex items-center gap-1.5 text-xs text-ochre-600 hover:text-ochre-800 font-medium"
        >
          <span className="material-icons text-sm">sms</span>
          Send timesheet link now to assigned workers
        </button>
      )}
    </div>
  );
}
